import type {
  GetMyCampaignStatusesResponse,
  GetMyCampaignStatusesResponse_ChargeBonusCampaignStatus,
} from "schema/gen/es/chiikipoint/wallet/v2/service_pb";
import {
  OSAKI_CHARGE_BONUS_CAMPAIGN_PERIODS,
  type OsakiChargeBonusCampaignTerm,
} from "../../config";
import { formatDate, toJSTDate } from "../../libs/date";
import { i18n, t } from "../../libs/i18n";

export const osakiChargeBonusUnitPrice = 5000;
export const osakiChargeBonusDedicatedPoint = 4000;
export const osakiChargeBonusCommonPoint = 3000;
export const osakiChargeBonusMaxQuantity = 2;

export type OsakiChargeBonusCampaignBannerType =
  | "hidden"
  | "not-applied"
  | "applied"
  | "loser"
  | "winner";

export type OsakiChargeBonusCampaignStatusLike = Pick<
  GetMyCampaignStatusesResponse_ChargeBonusCampaignStatus,
  "isApplied" | "isWinner" | "maxQuantity" | "consumedQuantity" | "fundSource"
>;

export type OsakiChargeBonusCampaignPhase = "application" | "purchase";

export type CurrentOsakiChargeBonusCampaignPeriod = {
  term: OsakiChargeBonusCampaignTerm;
  phase: OsakiChargeBonusCampaignPhase;
};

export function getOsakiChargeBonusCampaignTitle(
  term: OsakiChargeBonusCampaignTerm,
) {
  switch (term) {
    case "2nd":
      return t("おおさきポイントアップ商品券事業（２次募集）");
    case "3rd":
      return t("おおさきポイントアップ商品券事業（３次募集）");
    default:
      return t("おおさきポイントアップ商品券事業");
  }
}

export function getOsakiChargeBonusCampaignAboutTitle(
  term: OsakiChargeBonusCampaignTerm,
) {
  switch (term) {
    case "2nd":
      return t("おおさきポイントアップ商品券事業について（２次募集）");
    case "3rd":
      return t("おおさきポイントアップ商品券事業について（３次募集）");
    default:
      return t("おおさきポイントアップ商品券事業について");
  }
}

function getOsakiChargeBonusCampaignPeriod(term: OsakiChargeBonusCampaignTerm) {
  const period = OSAKI_CHARGE_BONUS_CAMPAIGN_PERIODS.find(
    (period) => period.term === term,
  );

  if (!period) {
    throw new Error(`unsupported osaki charge bonus campaign term: ${term}`);
  }

  return period;
}

function toFullWidthDigits(value: string) {
  return value.replace(/[0-9]/g, (digit) =>
    String.fromCharCode(digit.charCodeAt(0) + 0xfee0),
  );
}

// 和暦は日本市場向けの表記なので、他ロケールに持ち出さず ja のときだけ組み立てる
function formatCampaignDate(date: Date) {
  if (i18n.locale !== "ja") {
    return formatDate(date, "date");
  }
  // 端末TZの getter だと JST の日付境界をまたいだ瞬間に1日ずれる
  const jst = toJSTDate(date);
  const reiwaYear = jst.getFullYear() - 2018;

  return toFullWidthDigits(
    `令和${reiwaYear}年${jst.getMonth() + 1}月${jst.getDate()}日`,
  );
}

export function getOsakiChargeBonusApplicationDeadlineText(
  term: OsakiChargeBonusCampaignTerm,
) {
  return t("{date}まで", {
    date: formatCampaignDate(
      getOsakiChargeBonusCampaignPeriod(term).applicationEndDate,
    ),
  });
}

const JAPANESE_WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];

// 月日の漢字表記は日本市場向けなので、formatCampaignDate と同じく ja のときだけ組み立てる
function formatAnnouncementDate(date: Date, withWeekday: boolean) {
  if (i18n.locale !== "ja") {
    return formatDate(date, withWeekday ? "monthDayWeekday" : "monthDay");
  }
  // 端末TZの getter だと JST の日付境界をまたいだ瞬間に1日ずれる
  const jst = toJSTDate(date);
  const monthDay = `${jst.getMonth() + 1}月${jst.getDate()}日`;

  return toFullWidthDigits(
    withWeekday
      ? `${monthDay}（${JAPANESE_WEEKDAYS[jst.getDay()]}）`
      : monthDay,
  );
}

export function getOsakiChargeBonusLotteryResultAnnouncementDateText(
  term: OsakiChargeBonusCampaignTerm,
) {
  return formatAnnouncementDate(
    getOsakiChargeBonusCampaignPeriod(term).lotteryResultAnnouncementDate,
    true,
  );
}

export function getOsakiChargeBonusLotteryResultAnnouncementDateShortText(
  term: OsakiChargeBonusCampaignTerm,
) {
  return formatAnnouncementDate(
    getOsakiChargeBonusCampaignPeriod(term).lotteryResultAnnouncementDate,
    false,
  );
}

export function getOsakiChargeBonusAdditionalSaleNotice(
  term: OsakiChargeBonusCampaignTerm,
) {
  switch (term) {
    case "2nd":
      return t(
        "なお、未購入等により追加販売となった場合は３次募集を行います。３次募集のお知らせは、みやぎ県民公式アプリ「ポケットサイン」および大崎市公式LINEで通知します。",
      );
    case "3rd":
      return "";
    default:
      return t(
        "なお、未購入等により追加販売となった場合は2次募集を行います。2次募集のお知らせは、みやぎ県民公式アプリ「ポケットサイン」および大崎市公式LINEで通知します。",
      );
  }
}

export function getCurrentOsakiChargeBonusCampaignPeriod(
  now: Date,
): CurrentOsakiChargeBonusCampaignPeriod | null {
  for (const period of OSAKI_CHARGE_BONUS_CAMPAIGN_PERIODS) {
    if (
      now >= period.applicationStartDate &&
      now <= period.applicationEndDate
    ) {
      return { term: period.term, phase: "application" };
    }
    if (now >= period.purchaseStartDate && now <= period.purchaseEndDate) {
      return { term: period.term, phase: "purchase" };
    }
  }
  return null;
}

export function getCurrentOsakiChargeBonusCampaignStatus(
  campaignStatuses: GetMyCampaignStatusesResponse | null | undefined,
  now: Date,
): OsakiChargeBonusCampaignStatusLike | null {
  if (!campaignStatuses) {
    return null;
  }

  const currentPeriod = getCurrentOsakiChargeBonusCampaignPeriod(now);
  if (!currentPeriod) {
    return null;
  }

  switch (currentPeriod.term) {
    case "1st":
      return campaignStatuses.r8OsakiChargeBonus1st ?? null;
    case "2nd":
      return campaignStatuses.r8OsakiChargeBonus2nd ?? null;
    case "3rd":
      return campaignStatuses.r8OsakiChargeBonus3rd ?? null;
  }
}

export function getOsakiChargeBonusCampaignBannerType(
  status: OsakiChargeBonusCampaignStatusLike | null | undefined,
  isOsakiResident: boolean,
  now: Date,
): OsakiChargeBonusCampaignBannerType {
  if (!isOsakiResident || !status) {
    return "hidden";
  }

  const currentPeriod = getCurrentOsakiChargeBonusCampaignPeriod(now);
  if (!currentPeriod) {
    return "hidden";
  }

  if (currentPeriod.phase === "application") {
    return status.isApplied ? "applied" : "not-applied";
  }

  if (!status.isApplied) {
    return "hidden";
  }
  if (!status.isWinner) {
    return "loser";
  }
  if (getRemainingOsakiChargeBonusQuantity(status) <= 0) {
    return "hidden";
  }
  return "winner";
}

export function getRemainingOsakiChargeBonusQuantity(
  status: OsakiChargeBonusCampaignStatusLike | null | undefined,
) {
  if (!status) {
    return 0;
  }
  return Math.max(
    0,
    Number(status.maxQuantity) - Number(status.consumedQuantity),
  );
}

export function getRequiredOsakiChargeBonusPurchaseQuantity(
  status: OsakiChargeBonusCampaignStatusLike | null | undefined,
) {
  if (!status || Number(status.consumedQuantity) > 0) {
    return 0;
  }
  return Math.max(0, Number(status.maxQuantity));
}

export function canUseOsakiChargeBonusCharge(
  status: OsakiChargeBonusCampaignStatusLike | null | undefined,
  now: Date,
) {
  const currentPeriod = getCurrentOsakiChargeBonusCampaignPeriod(now);
  return (
    !!status &&
    currentPeriod?.phase === "purchase" &&
    status.isApplied &&
    status.isWinner &&
    getRequiredOsakiChargeBonusPurchaseQuantity(status) > 0
  );
}

export function calculateOsakiChargeBonus(quantity: number) {
  return {
    price: osakiChargeBonusUnitPrice * quantity,
    dedicatedPoint: osakiChargeBonusDedicatedPoint * quantity,
    commonPoint: osakiChargeBonusCommonPoint * quantity,
    totalPoint:
      (osakiChargeBonusDedicatedPoint + osakiChargeBonusCommonPoint) * quantity,
  };
}
